import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useDashboardPermissions, DashboardPermissions } from '@/hooks/useDashboardPermissions';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

type Flag = keyof Omit<DashboardPermissions, 'reasons'>;

/**
 * Wraps a dashboard route element. If the current user lacks the required
 * permission flag, redirects to /dashboard with a toast.
 * Waits for roles to finish loading before evaluating to avoid false redirects.
 */
export function PermissionRoute({ flag, children }: { flag: Flag; children: ReactNode }) {
  const { rolesLoading, user } = useAuth();
  const { permissions } = useDashboardPermissions();

  if (rolesLoading || !user) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!permissions[flag]) {
    toast.error("You don't have permission to access that page.");
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
